import { Box, Paper, Stack, TextField, Typography } from '@mui/material';
import { RANGE_PRESETS, presetRange, useDateRange } from '@/lib/useDateRange';
import { formatRange } from '@/lib/format';
import { Segmenty } from '@/components/Segmenty';

/**
 * Filtr okna czasowego. Świadomie minimalny — trzy gotowe okna i dwie daty.
 * Nie budujemy konstruktora zapytań; celem jest odpowiedź na pytanie, a nie narzędzie do zapytań.
 *
 * Okno porównawcze NIE jest konfigurowalne: zawsze równie długie i bezpośrednio poprzedzające.
 * Możliwość wyboru dowolnego okna porównawczego to najprostsza droga do fałszywego trendu.
 */
export function DateRangeFilter({
  comparison,
  kartaId,
  wariant = 'karta',
}: {
  comparison?: { date_from: string; date_to: string };
  /**
   * `karta`    — osobny blok na pełną szerokość pod nagłówkiem. Forma historyczna produktu.
   * `naglowek` — kolumna `.wybor-wariantu` z makiety, dociągnięta do prawej krawędzi wiersza
   *              nagłówka ekranu. Makieta trzyma sterowanie widokiem W nagłówku, nie pod nim:
   *              filtr rozciągnięty na całą szerokość czyta się jak treść ekranu, a nie jak
   *              jego ustawienie, i spycha pierwszą liczbę o ekran w dół.
   *
   * Wariant zmienia WYŁĄCZNIE układ. Te same trzy okna, te same dwa pola dat, ta sama logika
   * wyboru — inaczej byłyby to dwa filtry o jednej nazwie.
   */
  wariant?: 'karta' | 'naglowek';
  /**
   * Stabilny identyfikator karty dla walidacji DOM (R5-15) — ta sama konwencja co w
   * `PlytaGlowna`, `MiniMetryka`, `MetricCard` i `Karta`.
   *
   * Filtr jest WSPÓŁDZIELONY przez wszystkie ekrany z oknem czasowym, a w inwentarzu jest osobną
   * kartą na każdym z nich (na „Jakości danych" to DATA_QUALITY-10). Identyfikator zaszyty
   * w komponencie nadałby wszystkim tym wystąpieniom jeden adres i nie rozstrzygnąłby żadnego —
   * musi przyjść z miejsca użycia.
   *
   * Atrybut nie zmienia wyglądu ani zachowania. Jest wyłącznie adresem.
   */
  kartaId?: string;
}) {
  const [range, setRange] = useDateRange();

  /* Który preset odpowiada bieżącemu oknu. `null`, gdy żaden — wtedy segmenty nie zaznaczają
     niczego, zamiast wskazywać najbliższy i kłamać o zakresie. */
  const dniZakresu = Math.round(
    (Date.parse(`${range.date_to}T00:00:00Z`) - Date.parse(`${range.date_from}T00:00:00Z`)) / 86_400_000,
  ) + 1;
  const wybranePreset =
    RANGE_PRESETS.find((p) => p.days === dniZakresu) !== undefined ? String(dniZakresu) : null;

  const naglowek = wariant === 'naglowek';

  /*
   * OPIS OKNA — `.sprz-okno-opis` z makiety, `aria-live="polite"`.
   *
   * Mówi DOKŁADNIE tyle, ile wiadomo z samego zakresu: granice i liczbę dni kalendarzowych.
   * Makieta dopisuje tu jeszcze „dni z dostawami: X z Y" — filtr jest WSPÓLNY dla dwunastu
   * ekranów i nie zna żadnych danych, więc taka liczba musiałaby zostać zmyślona albo
   * przemycona z zewnątrz pod pozorem właściwości filtra. Podajemy podzbiór, który jest prawdziwy.
   *
   * Liczba dni bywa NaN, gdy użytkownik czyści pole daty — wtedy nie piszemy „NaN dni".
   */
  const opisOkna =
    Number.isFinite(dniZakresu) && dniZakresu > 0
      ? `${range.date_from} → ${range.date_to} · ${dniZakresu} dni kalendarzowych`
      : `${range.date_from} → ${range.date_to}`;

  const zawartosc = (
    <>
      {naglowek ? null : (
        <Typography variant="body2" color="text.secondary">
          Okno
        </Typography>
      )}

        {/*
          PRZEŁĄCZNIK SEGMENTOWY zamiast trzech niezależnych przycisków.

          Poprzednia forma nie miała STANU WYBRANEGO: audyt parytetu zapisał to zdaniem „na zrzucie
          nie widać, które okno jest aktywne". To nie usterka kosmetyczna — użytkownik czytał liczby,
          nie wiedząc, za jaki okres są policzone.

          Które okno jest wybrane, wyprowadzamy z FAKTYCZNEJ liczby dni zakresu, a nie z osobnego
          stanu komponentu. Osobny stan rozjeżdżałby się z adresem po wejściu w głęboki link
          albo po ręcznej zmianie daty — i pokazywałby „7 dni" przy oknie trzymiesięcznym.
        */}
        <Segmenty
          etykieta="Okno czasowe"
          wybrana={wybranePreset}
          onZmiana={(dni) => setRange(presetRange(Number(dni)))}
          opcje={RANGE_PRESETS.map((preset) => ({
            wartosc: String(preset.days),
            etykieta: preset.label,
          }))}
        />

        <TextField
          label="Od"
          type="date"
          size="small"
          value={range.date_from}
          onChange={(e) => setRange({ date_from: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Do"
          type="date"
          size="small"
          value={range.date_to}
          onChange={(e) => setRange({ date_to: e.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {comparison ? (
          <Typography variant="caption" color="text.secondary">
            porównanie: {formatRange(comparison.date_from, comparison.date_to)}
          </Typography>
        ) : null}
    </>
  );

  if (naglowek) {
    return (
      <Box
        data-cc-card-id={kartaId}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}
      >
        {/* `.karta-nadtytul` z makiety — nazwa sterowania stoi NAD nim, nie obok, bo obok
            konkurowałaby o miejsce z opcjami i łamała kolumnę przy węższym oknie. */}
        <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)', lineHeight: 1 }}>
          Okno czasu
        </Typography>

        <Stack
          direction="row"
          spacing={1.5}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}
        >
          {zawartosc}
        </Stack>

        <Typography
          aria-live="polite"
          sx={{
            m: 0,
            fontSize: 'var(--st-drobny)',
            color: 'var(--tekst-slaby)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {opisOkna}
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant="outlined" data-cc-card-id={kartaId} sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        {zawartosc}
      </Stack>
    </Paper>
  );
}
